import moment from "moment";

import { isValidEmail as isValidEmailShared } from "../../utils/email";
import {
  NotificationCategory,
  NotificationItem,
  NotificationPreferenceRow,
  Segment,
} from "./notifications.types";

const n = (text: string): Segment => ({ text });
const b = (text: string): Segment => ({ text, bold: true });

export const NOTIFICATION_CATEGORIES: {
  key: NotificationCategory;
  label: string;
  /** Settings window: what the row covers. */
  hint: string;
}[] = [
  {
    key: "mentions",
    label: "Mentions",
    hint: "Someone @mentions you in a note",
  },
  {
    key: "comments",
    label: "Comments",
    hint: "New notes on work and clients you follow",
  },
  {
    key: "assigned",
    label: "Assigned to me",
    hint: "You're assigned work or a subtask",
  },
  {
    key: "subtasks",
    label: "Completed subtasks",
    hint: "A subtask on your work is completed",
  },
  {
    key: "reminders",
    label: "Reminders",
    hint: "Reminders set on work items",
  },
  {
    key: "automations",
    label: "Automations",
    hint: "Automations and email sequence replies",
  },
  {
    key: "client_uploads",
    label: "Client uploads",
    hint: "A client sends files through the client portal",
  },
  {
    key: "signatures",
    label: "Signatures",
    hint: "A client signs a document you sent for e-signature",
  },
  {
    key: "client_alerts",
    label: "Client alerts",
    hint: "For clients you own: work overdue or stuck, invoices 30+ days late, no client manager",
  },
  {
    key: "client_requests",
    label: "Client requests",
    hint: "A client completes items of a request list, or hasn't after the reminders",
  },
];

export const categoryLabel = (key: NotificationCategory) =>
  NOTIFICATION_CATEGORIES.find((c) => c.key === key)?.label ?? key;

/** Old automations say only "You have received a new Notification regarding <client>." */
const GENERIC_AUTOMATION = /^You have received a new Notification regarding/i;

/**
 * What happened, after the person's name: "assigned you to [2025 T1]".
 * `who` is shown in bold before it (null = the sentence stands alone).
 */
export const notificationSentence = (
  item: NotificationItem,
): { who: string | null; what: Segment[] } => {
  const actor = item.actorName || "Someone";
  const work = item.workName || "a work item";
  const subtask = item.subtaskName || "a subtask";
  const client = item.clientName || "a client";

  switch (item.type) {
    case "WorkMention":
      return { who: actor, what: [n("mentioned you in a note on "), b(work)] };
    case "CustomerMention":
      return {
        who: actor,
        what: [n("mentioned you in a note on "), b(client)],
      };
    case "WorkComment":
      return { who: actor, what: [n("added a note on "), b(work)] };
    case "CustomerComment":
      return { who: actor, what: [n("added a note on "), b(client)] };
    case "AssignedWork":
      return { who: actor, what: [n("assigned you to "), b(work)] };
    case "AssignedSubTask":
      return {
        who: actor,
        what: [n("assigned you the subtask "), b(subtask), n(" on "), b(work)],
      };
    case "SubTaskCompleted":
      return {
        who: actor,
        what: [n("completed the subtask "), b(subtask), n(" on "), b(work)],
      };
    case "JobReminder":
      return { who: null, what: [n("Reminder for "), b(work)] };
    case "ClientUpload": {
      const count = Math.max(item.fileCount, item.files.length, 1);
      return {
        who: item.actorName || "Your client",
        what: [
          n("uploaded "),
          b(count === 1 ? "a document" : `${count} documents`),
          n(" in the client portal"),
        ],
      };
    }
    case "ClientSigned": {
      // Server message: 'signed "Letter.pdf". Everyone has signed.' (owner 2026-10-28)
      const message = (item.message || "").trim();
      const match = /^signed "(.*)"\.\s*(.*)$/s.exec(message);
      const who = item.actorName || "Your client";
      if (!match) return { who, what: [n(message || "signed a document")] };
      return {
        who,
        what: [n("signed "), b(match[1]), n(match[2] ? `. ${match[2]}` : "")],
      };
    }
    case "ClientAlert":
      // The server writes the whole sentence (client team, Mg-231).
      return {
        who: null,
        what: [
          n((item.message || "").trim() || "A client needs your attention"),
        ],
      };
    case "ClientRequest": {
      // Server message: 'sent everything for "October documents"', 'completed 2 items of "…" (2 of 4 done)',
      // or (no client name) 'ABC Corp hasn't completed "…" after 6 reminders'.
      const message = (item.message || "").trim() || "updated a request list";
      return { who: item.actorName || null, what: [n(message)] };
    }
    case "WorkAutomation":
    case "WorkTaskAutomation":
    default: {
      const message = (item.message || "").trim();
      if (!message || GENERIC_AUTOMATION.test(message)) {
        return {
          who: null,
          what: item.workName
            ? [n("An automation sent you a notification about "), b(work)]
            : [n("An automation sent you a notification")],
        };
      }
      return { who: null, what: [n(message)] };
    }
  }
};

/** Plain text of a notification (for titles and screen readers). */
export const notificationText = (item: NotificationItem) => {
  const { who, what } = notificationSentence(item);
  return `${who ? `${who} ` : ""}${what.map((s) => s.text).join("")}`;
};

/** Link to the client's profile (organization or individual), or null. */
export const clientLink = (item: NotificationItem): string | null => {
  if (item.businessContactId) return `/organization/${item.businessContactId}`;
  if (item.clientId && item.contactId)
    return `/contact/${item.clientId}?contactId=${item.contactId}`;
  return null;
};

/** The client's Client portal tab, Signatures section. */
export const clientSignaturesLink = (item: NotificationItem): string | null => {
  if (item.businessContactId)
    return `/organization/${item.businessContactId}/portal?view=signatures`;
  if (item.clientId && item.contactId)
    return `/contact/${item.clientId}/portal?contactId=${item.contactId}&view=signatures`;
  return null;
};

/** Where clicking the notification goes. Client uploads open the client (their Files card). */
export const notificationLink = (item: NotificationItem): string | null => {
  if (item.type === "ClientAlert") {
    if (item.invoiceId) return `/billing/invoices/${item.invoiceId}`;
    if (item.workId) return `/work/${item.workId}`;
    return clientLink(item);
  }
  if (item.type === "ClientSigned")
    return clientSignaturesLink(item) ?? clientLink(item);
  if (
    item.type === "ClientUpload" ||
    item.type === "CustomerComment" ||
    item.type === "CustomerMention"
  )
    return clientLink(item) ?? (item.workId ? `/work/${item.workId}` : null);
  if (item.type === "ClientRequest" && item.workId)
    return `/work/${item.workId}/requests`;
  if (item.workId) return `/work/${item.workId}`;
  return clientLink(item);
};

/** The button on the row: what the person most likely wants to do next. */
export const primaryActionLabel = (item: NotificationItem) => {
  switch (item.category) {
    case "client_uploads":
      return "Review files";
    case "signatures":
      return "View signatures";
    case "client_alerts":
      if (item.alert === "invoice_late" || item.invoiceId)
        return "Open invoice";
      if (item.alert === "no_manager") return "Pick a manager";
      return item.workId ? "Open work" : "Open client";
    case "client_requests":
      return "View request";
    case "mentions":
    case "comments":
      return "View note";
    default:
      return item.workId ? "Open work" : "Open";
  }
};

/** "9:42 AM" today, "Yesterday", "Mon" this week, else "Oct 17" (or "Oct 17, 2025" in another year). */
export const notificationTimeLabel = (date: string, now = moment()) => {
  const when = moment(date);
  const day = when.clone().startOf("day");
  const today = now.clone().startOf("day");
  if (day.isSame(today)) return when.format("h:mm A");
  if (day.isSame(today.clone().subtract(1, "day"))) return "Yesterday";
  if (today.diff(day, "days") < 7) return when.format("ddd");
  if (when.year() === now.year()) return when.format("MMM D");
  return when.format("MMM D, YYYY");
};

/** Full date and time for the tooltip: "Oct 19, 2026 · 2:42 AM". */
export const notificationFullTime = (date: string) =>
  moment(date).format("MMM D, YYYY · h:mm A");

export type NotificationGroup = { label: string; items: NotificationItem[] };

/** Today / Yesterday / Earlier this week / Earlier, keeping the order (newest first). */
export const groupNotifications = (
  items: NotificationItem[],
  now = moment(),
): NotificationGroup[] => {
  const today = now.clone().startOf("day");
  const label = (date: string) => {
    const day = moment(date).startOf("day");
    if (!day.isBefore(today)) return "Today";
    if (day.isSame(today.clone().subtract(1, "day"))) return "Yesterday";
    if (today.diff(day, "days") < 7) return "Earlier this week";
    return "Earlier";
  };
  const groups: NotificationGroup[] = [];
  items.forEach((item) => {
    const name = label(item.date);
    const last = groups[groups.length - 1];
    if (last && last.label === name) last.items.push(item);
    else groups.push({ label: name, items: [item] });
  });
  return groups;
};

export const initials = (name?: string | null) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (
    (parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")
  ).toUpperCase();
};

/** Badge text: "99+" above 99, nothing at 0. */
export const badgeText = (count?: number) =>
  !count ? "" : count > 99 ? "99+" : String(count);

/** Settings rows in the page's order, with defaults for anything missing. */
export const settingsRows = (
  rows: NotificationPreferenceRow[] | undefined,
): NotificationPreferenceRow[] =>
  NOTIFICATION_CATEGORIES.map(({ key }) => {
    const row = rows?.find((r) => r.event === key);
    const locked = key === "mentions" || key === "automations";
    return {
      event: key,
      inApp: locked || (row?.inApp ?? true),
      email: row?.email ?? true,
      inAppLocked: locked,
    };
  });

/** The app's shared email check (QA batch 12). */
export const isValidEmail = (value: string) => isValidEmailShared(value);

/** Error text from an RTK Query error (the API answers plain-English messages). */
export const errorText = (error: unknown, fallback: string) => {
  const data = (error as { data?: unknown } | undefined)?.data;
  return typeof data === "string" && data.length < 300 ? data : fallback;
};

/** The all-done answer of new ({count, ids}) and older (a number) servers. */
export const allDoneResult = (
  raw: number | { count?: number; ids?: string[] } | null | undefined,
): { count: number; ids: string[] } => {
  if (typeof raw === "number") return { count: raw, ids: [] };
  const ids = Array.isArray(raw?.ids)
    ? raw!.ids.filter((id): id is string => typeof id === "string")
    : [];
  return {
    count: typeof raw?.count === "number" ? raw.count : ids.length,
    ids,
  };
};

/** Splits ids into requests of at most `size` (the server takes 500 at once). */
export const chunkIds = (ids: string[], size = 500): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size)
    chunks.push(ids.slice(i, i + size));
  return chunks;
};

/**
 * The confirm question for "Mark all as done". `count` = how many will move
 * (null when not known exactly, e.g. a category with more than is loaded).
 */
/**
 * The "Mark all as done" question. The whole inbox says "in your inbox" and,
 * when known, how many of them are unread: "25 notifications" next to an
 * Inbox badge of 11 read as a mismatch (the badge counts unread ones; QA batch 11).
 */
export const markAllDoneQuestion = (
  count: number | null,
  label?: string,
  unread?: number | null,
) => {
  const where = label ? ` in ${label}` : " in your inbox";
  const unreadNote =
    typeof unread === "number" && count !== null && count > 0
      ? ` (${unread} unread)`
      : "";
  if (count === null) return `Move all notifications${where} to Done?`;
  if (count === 1) return `Move 1 notification${where} to Done?${unreadNote}`;
  return `Move all ${count} notifications${where} to Done?${unreadNote}`;
};
